import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton,
  Chip,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Restaurant as NutritionIcon,
  FitnessCenter as CardioIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { getCardioSessionsPerWeek } from "../../../../../utils/cardioGoalDisplay";
import useWaterUnit from "../../../../../hooks/useWaterUnit";

// Custom styled components
const StyledProgressCard = styled(Card)(({ theme }) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(2),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  transition: "box-shadow 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  },
}));

const GoalHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const GoalContent = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  flexGrow: 1,
  minHeight: 0,
  padding: theme.spacing(3),
  paddingTop: theme.spacing(2),
  "&:last-child": {
    paddingBottom: theme.spacing(3),
  },
}));

const MetricTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    border: "none",
    padding: theme.spacing(1),
  },
  "& .MuiTableCell-head": {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
}));

const ActionButton = styled(Button)(() => ({
  borderRadius: 20,
  padding: "8px 20px",
  textTransform: "none",
  fontWeight: 600,
}));

/** Pinned footer: divider + actions align across side-by-side goal cards */
const GoalCardFooter = ({ children }) => (
  <Box sx={{ flexShrink: 0, width: "100%" }}>
    <Divider sx={{ my: 2 }} />
    <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
      {children}
    </Stack>
  </Box>
);

// Enhanced Nutrition Goals Component
const EnhancedNutritionGoals = ({
  client,
  openMacroDialog,
  openNutritionGoalsDialog,
}) => {
  const { waterLabel } = useWaterUnit();
  const activeGoal = client?.nutritionGoals?.find((goal) => goal.active);
  const lastChanged = client?.nutritionGoalChanged;

  // Menu state for the three-dot button
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <StyledProgressCard>
      <GoalHeader>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <NutritionIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Nutrition Goals
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            aria-controls={menuOpen ? "nutrition-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
          >
            <MoreIcon />
          </IconButton>
          <Menu
            id="nutrition-menu"
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "nutrition-goals-button",
            }}
          >
            {activeGoal && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openMacroDialog(activeGoal);
                }}
              >
                Edit Current Goal
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openNutritionGoalsDialog();
              }}
            >
              View All Nutrition Goals
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openMacroDialog();
              }}
            >
              Add New Goal
            </MenuItem>
          </Menu>
        </Stack>
      </GoalHeader>

      <GoalContent>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {lastChanged && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Last Changed:{" "}
              {lastChanged.toDate
                ? lastChanged.toDate().toLocaleDateString()
                : "Unknown"}
            </Typography>
          )}

          {activeGoal ? (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {activeGoal.name}
                </Typography>
                <Chip
                  label="Active"
                  color="success"
                  variant="outlined"
                  size="small"
                />
              </Stack>

              <MetricTable size="small">
                <TableBody>
                  {activeGoal.proteins !== undefined &&
                    activeGoal.proteins !== null && (
                      <TableRow>
                        <TableCell>Protein</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {activeGoal.proteins.toFixed(0)}g
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  {activeGoal.carbs !== undefined &&
                    activeGoal.carbs !== null && (
                      <TableRow>
                        <TableCell>Carbs</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {activeGoal.carbs.toFixed(0)}g
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  {activeGoal.fats !== undefined && activeGoal.fats !== null && (
                    <TableRow>
                      <TableCell>Fats</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {activeGoal.fats.toFixed(0)}g
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {activeGoal.water !== undefined &&
                    activeGoal.water !== null && (
                      <TableRow>
                        <TableCell>Water</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {waterLabel(activeGoal.water)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  <TableRow>
                    <TableCell>Fiber</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {Number(activeGoal.fiber ?? 0).toFixed(1)}g
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {activeGoal.kcals !== undefined &&
                    activeGoal.kcals !== null && (
                      <TableRow>
                        <TableCell>Calories</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {activeGoal.kcals}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </MetricTable>
            </Box>
          ) : (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "grey.50",
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: "grey.300",
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                No nutrition goals set
              </Typography>
            </Paper>
          )}
        </Box>

        <GoalCardFooter>
          <ActionButton
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openMacroDialog()}
          >
            Add Goal
          </ActionButton>
        </GoalCardFooter>
      </GoalContent>
    </StyledProgressCard>
  );
};

// Enhanced Cardio Goals Component
const EnhancedCardioGoals = ({
  client,
  openCardioDialog,
  openCardioGoalsDialog,
}) => {
  const activeGoal = client?.cardioGoals?.find((goal) => goal.active);
  const lastChanged = client?.cardioGoalChanged;

  // Menu state for the three-dot button
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <StyledProgressCard>
      <GoalHeader>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CardioIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Cardio Goals
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            aria-controls={menuOpen ? "cardio-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
          >
            <MoreIcon />
          </IconButton>
          <Menu
            id="cardio-menu"
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{
              "aria-labelledby": "cardio-goals-button",
            }}
          >
            {activeGoal && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openCardioDialog(activeGoal);
                }}
              >
                Edit Current Goal
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openCardioGoalsDialog();
              }}
            >
              View All Cardio Goals
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                openCardioDialog();
              }}
            >
              Add New Goal
            </MenuItem>
          </Menu>
        </Stack>
      </GoalHeader>

      <GoalContent>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {lastChanged && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 2, display: "block" }}
            >
              Last Changed:{" "}
              {lastChanged.toDate
                ? lastChanged.toDate().toLocaleDateString()
                : "Unknown"}
            </Typography>
          )}

          {activeGoal ? (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {activeGoal.name}
                </Typography>
                <Chip
                  label="Active"
                  color="success"
                  variant="outlined"
                  size="small"
                />
              </Stack>

              <MetricTable size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Sessions / week</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {getCardioSessionsPerWeek(activeGoal) ?? 3}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Duration</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {activeGoal.duration || "30 minutes"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Intensity</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {activeGoal.intensity || "Medium"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Target H/R</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {activeGoal.targetHR || "120-140"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </MetricTable>
            </Box>
          ) : (
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                bgcolor: "grey.50",
                borderStyle: "dashed",
                borderWidth: 2,
                borderColor: "grey.300",
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                No cardio goals set
              </Typography>
            </Paper>
          )}
        </Box>

        <GoalCardFooter>
          <ActionButton
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => openCardioDialog()}
          >
            Add Goal
          </ActionButton>
        </GoalCardFooter>
      </GoalContent>
    </StyledProgressCard>
  );
};

// Enhanced Progress Tab Component
const EnhancedProgressTab = ({ client, handlers }) => {
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
          <EnhancedNutritionGoals
            client={client}
            onDelete={handlers.handleNutritionGoalDelete}
            onActivate={handlers.handleNutritionGoalActivate}
            onEdit={handlers.handleNutritionGoalEdit}
            openMacroDialog={handlers.openMacroDialog}
            openNutritionGoalsDialog={handlers.openNutritionGoalsDialog}
          />
        </Grid>
        <Grid item xs={12} lg={6} sx={{ display: "flex" }}>
          <EnhancedCardioGoals
            client={client}
            onDelete={handlers.handleCardioGoalDelete}
            onActivate={handlers.handleCardioGoalActivate}
            onEdit={handlers.handleCardioGoalEdit}
            openCardioDialog={handlers.openCardioDialog}
            openCardioGoalsDialog={handlers.openCardioGoalsDialog}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default EnhancedProgressTab;
